import BaseEntity from '@/entities/BaseEntity';
import { isConditionGroup } from '@/utils/conditions.js';
import { freeze } from '@/utils/object.js';

const CONDITION_FIELDS = [ 'field.conditions', 'field.tasks.conditions' ];

export default class RuleChange extends BaseEntity {
  field = null;
  original = null;
  new = null;
  conditionDiff = new Map();

  constructor(rawData) {
    super();
    this.updateWith(rawData);

    this.conditionDiff = this.isCondition
      ? diffConditionTrees(this.original, this.new)
      : new Map();
  }

  get isCondition() {
    return CONDITION_FIELDS.includes(this.field);
  }
}

export const LEAF_STATUS = freeze({
  UNCHANGED: 'unchanged',
  MODIFIED: 'modified',
  ADDED: 'added',
  REMOVED: 'removed',
});

/**
 * Diff two condition trees at the leaf level and return a Map keyed by leaf
 * node reference → 'unchanged' | 'modified' | 'added' | 'removed'.
 *
 *  - 'unchanged': the leaf has an identical twin (full serialization) on the
 *    other side.
 *  - 'modified':  the same identity (field + subfield + date_period) exists on
 *    both sides, but the operator / threshold / settings differ.
 *  - 'added':     the leaf exists only in the new tree.
 *  - 'removed':   the leaf exists only in the original tree.
 *
 * Matching is greedy and position-independent, so reordering a condition does
 * not surface as a change. The returned map covers both trees' leaves; group
 * nodes are intentionally left out (rendered as neutral).
 */
function diffConditionTrees(originalTree, newTree) {
  const status = new Map();

  const origLeaves = flattenConditionLeaves(originalTree).map(node => ({ node, used: false }));
  const newLeaves = flattenConditionLeaves(newTree).map(node => ({ node, used: false }));

  // Pass 1 — exact matches (identical definition) are unchanged on both sides.
  newLeaves.forEach(entry => {
    const serial = serializeConditionLeaf(entry.node);
    const match = origLeaves.find(candidate =>
      !candidate.used && serializeConditionLeaf(candidate.node) === serial);

    if (match) {
      entry.used = true;
      match.used = true;
      status.set(entry.node, LEAF_STATUS.UNCHANGED);
      status.set(match.node, LEAF_STATUS.UNCHANGED);
    }
  });

  // Pass 2 — same identity among the leftovers means the condition was modified.
  newLeaves.forEach(entry => {
    if (entry.used) {
      return;
    }

    const identity = conditionLeafIdentity(entry.node);
    const match = origLeaves.find(candidate =>
      !candidate.used && conditionLeafIdentity(candidate.node) === identity);

    if (match) {
      entry.used = true;
      match.used = true;
      status.set(entry.node, LEAF_STATUS.MODIFIED);
      status.set(match.node, LEAF_STATUS.MODIFIED);
    }
  });

  // Pass 3 — whatever is left exists on only one side.
  newLeaves.forEach(entry => {
    if (!entry.used) {
      status.set(entry.node, LEAF_STATUS.ADDED);
    }
  });
  origLeaves.forEach(entry => {
    if (!entry.used) {
      status.set(entry.node, LEAF_STATUS.REMOVED);
    }
  });

  // Pass 4 — flag groups whose AND/OR logic flipped. Aligned positionally
  // (good for the common case: same structure, logic changed). Only marks a
  // group when both aligned sides are groups with a differing logic.
  markGroupLogicChanges(originalTree, newTree, status);

  return status;
}

function conditionLeafIdentity(node) {
  if (!node || isConditionGroup(node)) {
    return null;
  }

  return [
    node.field ?? '',
    node.subfield ?? '',
    node.date_period ?? '',
  ].join('');
}

function flattenConditionLeaves(node, acc = []) {
  if (!node) {
    return acc;
  }

  if (isConditionGroup(node)) {
    (node.items || []).forEach(item => flattenConditionLeaves(item, acc));
  }
  else {
    acc.push(node);
  }

  return acc;
}

function markGroupLogicChanges(origNode, newNode, status) {
  if (!isConditionGroup(origNode) || !isConditionGroup(newNode)) {
    return;
  }

  if (origNode.logic !== newNode.logic) {
    const transition = { from: origNode.logic, to: newNode.logic };
    status.set(origNode, transition);
    status.set(newNode, transition);
  }

  const origItems = origNode.items || [];
  const newItems = newNode.items || [];
  const length = Math.min(origItems.length, newItems.length);

  for (let i = 0; i < length; i++) {
    markGroupLogicChanges(origItems[i], newItems[i], status);
  }
}

function serializeConditionLeaf(node) {
  if (!node || isConditionGroup(node)) {
    return null;
  }

  const settings = node.settings || {};

  return [
    node.field ?? '',
    node.subfield ?? '',
    node.date_period ?? '',
    node.comparison ?? '',
    JSON.stringify(node.value ?? ''),
    settings.dynamic_operator_direction ?? '',
    settings.dynamic_operator_area ?? '',
  ].join('');
}
